import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};
import { AuthProvider, useAuth } from './context/AuthContext';
import { FavoritesProvider } from './context/FavoritesContext';
import { NotificationsProvider } from './context/NotificationsContext';
import { Toaster } from './components/ui/sonner';
import Header from './components/Header';
import { Footer } from './components/Footer';
import HomePage from './pages/HomePage';
import { LoginPage, RegisterPage } from './pages/AuthPages';
import CataloguePage from './pages/CataloguePage';
import CreateListingPage from './pages/CreateListingPage';
import ListingDetailPage from './pages/ListingDetailPage';
import OrderSuccessPage from './pages/OrderSuccessPage';
import DashboardPage from './pages/DashboardPage';
import AdminPage from './pages/AdminPage';
import FavoritesPage from './pages/FavoritesPage';
import CheckoutPage from './pages/CheckoutPage';
import SellerProfilePage from './pages/SellerProfilePage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import CGVPage from './pages/CGVPage';
import MentionsLegalesPage from './pages/MentionsLegalesPage';
import { HowItWorksPage } from './pages/HowItWorksPage';
import EstimatePage from './pages/EstimatePage';
import BlogPage from './pages/BlogPage';
import BlogArticlePage from './pages/BlogArticlePage';
import GuidePage from './pages/GuidePage';
import PadProPage from './pages/PadProPage';
import { useRealtimeNotifications } from './hooks/useRealtimeNotifications';
import AnalyticsTracker from './components/AnalyticsTracker';
import { SeoLang } from './components/SeoLang';
import { langBasename } from './i18n';

// Component to initialize real-time notifications
const RealtimeNotificationsProvider = ({ children }) => {
  useRealtimeNotifications();
  return children;
};

const Layout = ({ children }) => (
  <>
    <Header />
    <main className="min-h-[calc(100vh-64px)]">{children}</main>
    <Footer />
  </>
);

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#0C63BF] border-t-transparent rounded-full animate-spin" /></div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#0C63BF] border-t-transparent rounded-full animate-spin" /></div>;
  if (!isAuthenticated || !isAdmin) return <Navigate to="/" replace />;
  return children;
};

function App() {
  return (
    <BrowserRouter basename={langBasename}>
      <AuthProvider>
        <FavoritesProvider>
        <NotificationsProvider>
        <RealtimeNotificationsProvider>
        <ScrollToTop />
        <SeoLang />
        <AnalyticsTracker />
        <Toaster position="top-right" />
        <Routes>
          <Route path="/" element={<Layout><HomePage /></Layout>} />
          <Route path="/login" element={<Layout><LoginPage /></Layout>} />
          <Route path="/register" element={<Layout><RegisterPage /></Layout>} />
          <Route path="/forgot-password" element={<Layout><ForgotPasswordPage /></Layout>} />
          <Route path="/reset-password" element={<Layout><ResetPasswordPage /></Layout>} />
          <Route path="/verify-email" element={<Layout><VerifyEmailPage /></Layout>} />
          <Route path="/cgv" element={<Layout><CGVPage /></Layout>} />
          <Route path="/mentions-legales" element={<Layout><MentionsLegalesPage /></Layout>} />
          <Route path="/comment-ca-marche" element={<Layout><HowItWorksPage /></Layout>} />
          <Route path="/estimer" element={<Layout><EstimatePage /></Layout>} />
          <Route path="/blog" element={<Layout><BlogPage /></Layout>} />
          <Route path="/blog/:slug" element={<Layout><BlogArticlePage /></Layout>} />
          <Route path="/guide" element={<Layout><GuidePage /></Layout>} />
          <Route path="/padpro" element={<Layout><PadProPage /></Layout>} />
          <Route path="/catalogue" element={<Layout><CataloguePage /></Layout>} />
          <Route path="/annonce/:id" element={<Layout><ListingDetailPage /></Layout>} />
          <Route path="/vendeur/:id" element={<Layout><SellerProfilePage /></Layout>} />
          <Route path="/vendre" element={<Layout><CreateListingPage /></Layout>} />
          <Route path="/order/success/:orderId" element={<Layout><OrderSuccessPage /></Layout>} />
          <Route path="/order/success" element={<Layout><OrderSuccessPage /></Layout>} />
          <Route path="/favoris" element={<ProtectedRoute><Layout><FavoritesPage /></Layout></ProtectedRoute>} />
          <Route path="/checkout/:id" element={<ProtectedRoute><Layout><CheckoutPage /></Layout></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Layout><DashboardPage /></Layout></ProtectedRoute>} />
          <Route path="/admin" element={<AdminRoute><Layout><AdminPage /></Layout></AdminRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </RealtimeNotificationsProvider>
        </NotificationsProvider>
        </FavoritesProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
